import { logActivity } from '../utils/activityLog';
import { fmtDate } from '../utils/format';
import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Filter, Pencil, Trash2, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import api from '../services/api';
import { toast } from 'sonner';

import {
  getAssignableRoleOptions,
  getCurrentUser as getUser,
  getHeadRoles,
  getRoleDepartments,
  getRoleLabels,
  getTeamRoles,
  hasPermission,
} from '../utils/roles';

const ROLES = [
  { value: 'admin',         label: 'Admin' },
  { value: 'crm_manager',   label: 'CRM Manager' },
  { value: 'head_of_sales', label: 'Head of Sales' },
  { value: 'sales_team',    label: 'TL Sales' },
  { value: 'sales_agent',   label: 'Sales Agent' },
  { value: 'head_of_ret',   label: 'Head of Retention' },
  { value: 'ret_team',      label: 'TL Retention' },
  { value: 'ret_agent',     label: 'Ret Agent' },
];

const ROLE_LABEL = {
  owner: 'Owner',
  ...Object.fromEntries(ROLES.map(role => [role.value, role.label])),
};
const ROLE_COLOR = {
  owner:          'bg-purple-500/20 text-purple-300 border-purple-500/30',
  admin:          'bg-red-500/20 text-red-400 border-red-500/30',
  crm_manager:    'bg-orange-500/20 text-orange-400 border-orange-500/30',
  head_of_sales:  'bg-green-500/20 text-green-400 border-green-500/30',
  sales_team:     'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  sales_agent:    'bg-teal-500/20 text-teal-400 border-teal-500/30',
  head_of_ret:    'bg-blue-500/20 text-blue-400 border-blue-500/30',
  ret_team:       'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  ret_agent:      'bg-sky-500/20 text-sky-400 border-sky-500/30',
};

const STATUS_BADGE = (active) =>
  active
    ? 'bg-green-500/20 text-green-400 border border-green-500/30'
    : 'bg-red-500/20 text-red-400 border border-red-500/30';

// Auto-derive department from role
const ROLE_DEPT = {
  owner:         'admin',
  admin:         'admin',
  crm_manager:   'admin',
  head_of_sales: 'sales',
  sales_team:    'sales',
  sales_agent:   'sales',
  head_of_ret:   'retention',
  ret_team:      'retention',
  ret_agent:     'retention',
};

const EMPTY_FORM = {
  name: '', email: '', password: '', role: 'sales_agent', department: 'sales',
  office: '', desk: '', team: '', desks: [],
};

export default function Users() {
  const { theme } = useTheme();
  const { t } = useLanguage();
  const isDark = theme === 'dark';
  const currentUser = getUser();
  const isOwner = currentUser.role === 'owner';
  const myOffice = currentUser.office || '';
  const canCreateUser = hasPermission(currentUser, 'Create User');
  const canEditUser = hasPermission(currentUser, 'Edit User');
  const canDeleteUser = hasPermission(currentUser, 'Delete User');
  const roleOptions = getAssignableRoleOptions();
  const roleLabels = { ...ROLE_LABEL, ...getRoleLabels() };
  const roleDept = { ...ROLE_DEPT, ...getRoleDepartments() };
  const headRoles = getHeadRoles();
  const teamRoles = getTeamRoles();

  const [users,       setUsers]       = useState([]);
  const [total,       setTotal]       = useState(0);
  const [page,        setPage]        = useState(1);
  const [totalPages,  setTotalPages]  = useState(1);
  const [loading,     setLoading]     = useState(true);
  const [activeFilter, setActiveFilter] = useState(null);
  const [showFilters,  setShowFilters]  = useState(false);
  const [filters, setFilters] = useState({ search: '', role: '', office: '', desk: '', team: '' });

  const [showModal, setShowModal] = useState(false);
  const [editUser,  setEditUser]  = useState(null);
  const [form,      setForm]      = useState(EMPTY_FORM);

  // ── Hierarchy options ────────────────────────────────────────────────────
  const [officeOpts,   setOfficeOpts]   = useState([]);  // [{id, name}]
  const [allTeamOpts,  setAllTeamOpts]  = useState([]);  // all teams — for TL/Agent modal

  // For filter panel cascade
  const [filterDeskOpts, setFilterDeskOpts] = useState([]);
  const [filterTeamOpts, setFilterTeamOpts] = useState([]);

  const cls = {
    card:       `rounded-lg border ${isDark ? 'bg-[#0f1729] border-gray-800' : 'bg-white border-gray-200'}`,
    input:      `w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${isDark ? 'bg-[#0a0e1a] border-gray-700 text-white placeholder-gray-600' : 'bg-white border-gray-300 text-gray-900'}`,
    inputDis:   `w-full px-3 py-2 border rounded-md text-sm ${isDark ? 'bg-[#060a14] border-gray-800 text-gray-600' : 'bg-gray-100 border-gray-200 text-gray-400'} cursor-not-allowed`,
    label:      `block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`,
    th:         `px-3 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`,
    td:         `px-3 py-3 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`,
    btn:        `px-3 py-1.5 text-sm border rounded-md transition-colors ${isDark ? 'border-gray-700 text-gray-300 hover:bg-[#2a2f4a]' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`,
    btnPrimary: 'px-4 py-2 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors',
  };

  useEffect(() => { logActivity('view', 'users', null, 'Viewed Users list'); }, []);
  useEffect(() => {
    api.get('/offices').then(({ data }) => setOfficeOpts(data.map(o => ({ id: o.id, name: o.name }))));
    api.get('/teams').then(({ data }) => setAllTeamOpts(data));
  }, []);
  useEffect(() => { loadUsers(); }, [page, activeFilter]); // eslint-disable-line

  // ── Cascade: filter office → desks ──────────────────────────────────────
  useEffect(() => {
    setFilters(p => ({ ...p, desk: '', team: '' }));
    setFilterDeskOpts([]);
    setFilterTeamOpts([]);
    if (!filters.office) return;
    const o = officeOpts.find(x => x.name === filters.office);
    if (!o) return;
    api.get('/desks', { params: { office_id: o.id } })
      .then(({ data }) => setFilterDeskOpts(data));
  }, [filters.office]); // eslint-disable-line

  // ── Cascade: filter desk → teams ────────────────────────────────────────
  useEffect(() => {
    setFilters(p => ({ ...p, team: '' }));
    setFilterTeamOpts([]);
    if (!filters.desk) return;
    const d = filterDeskOpts.find(x => x.name === filters.desk);
    if (!d) return;
    api.get('/teams', { params: { desk_id: d.id } })
      .then(({ data }) => setFilterTeamOpts(data));
  }, [filters.desk]); // eslint-disable-line

  const loadUsers = async () => {
    try {
      setLoading(true);
      const params = { page, limit: 25 };
      if (activeFilter !== null) params.active = activeFilter;
      if (filters.search) params.search = filters.search;
      if (filters.role)   params.role   = filters.role;
      if (filters.office) params.office = filters.office;
      if (filters.desk)   params.desk   = filters.desk;
      const { data } = await api.get('/users', { params });
      const fetchedUsers = data.users || [];
      setUsers(isOwner ? fetchedUsers : fetchedUsers.filter(u => u.role !== 'owner'));
      setTotal(data.total);
      setTotalPages(data.totalPages);
    } catch { toast.error(t('users.failedLoad')); }
    finally { setLoading(false); }
  };

  const applyFilters = () => { setPage(1); loadUsers(); };

  const openNew = () => {
    if (!canCreateUser) return;
    setEditUser(null);
    setForm({ ...EMPTY_FORM, office: isOwner ? '' : myOffice });
    setShowModal(true);
  };

  const openEdit = (u) => {
    if (!canEditUser) return;
    setEditUser(u);
    setForm({ name: u.name, email: u.email, password: '', role: u.role,
              department: u.department || roleDept[u.role] || '',
              office: u.office || (isOwner ? '' : myOffice),
              desk: u.desk || '', team: u.team || '', desks: [] });
    setShowModal(true);
  };

  const saveUser = async () => {
    if (editUser && !canEditUser) return;
    if (!editUser && !canCreateUser) return;
    if (!form.name || !form.email) { toast.error(t('users.nameEmailRequired')); return; }
    try {
      const role = editUser?.role === 'owner' ? 'owner' : form.role;
      const isHead = headRoles.includes(role);
      const payload = {
        name: form.name, email: form.email, role,
        password: editUser && form.password ? form.password : undefined,
        department: form.department || null,
        office: form.office || null,
        desk: isHead ? null : (form.desk || null),
        team: isHead ? null : (form.team || null),
      };
      if (editUser) {
        await api.put(`/users/${editUser.id}`, payload);
        if (String(editUser.id) === String(currentUser.id)) {
          window.dispatchEvent(new Event('current-user-updated'));
        }
        toast.success(t('users.userUpdated'));
      } else {
        if (!form.password) { toast.error(t('users.passwordRequired')); return; }
        await api.post('/users', { ...payload, password: form.password });
        toast.success(t('users.userCreated'));
      }
      setShowModal(false);
      loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || t('users.saveFailed')); }
  };

  const toggleActive = async (u) => {
    if (!canEditUser) return;
    try {
      await api.patch(`/users/${u.id}/toggle`);
      toast.success(u.active ? t('users.userDeactivated') : t('users.userActivated'));
      loadUsers();
    } catch { toast.error(t('users.statusUpdateFailed')); }
  };

  const deleteUser = async (u) => {
    if (!canDeleteUser) return;
    if (!window.confirm(t('users.confirmDelete').replace('{name}', u.name))) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(t('users.userDeleted'));
      loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || t('users.deleteFailed')); }
  };

  const filterCount = Object.values(filters).filter(Boolean).length;

  // ── Helper: select with disabled state ──────────────────────────────────
  const HierarchySelect = ({ label, value, onChange, options, disabled, placeholder }) => (
    <div>
      <label className={cls.label}>{label}</label>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
        className={disabled ? cls.inputDis : cls.input}
      >
        <option value="">{disabled ? '— select parent first —' : placeholder}</option>
        {options.map(o => <option key={o.id} value={o.name}>{o.name}</option>)}
      </select>
    </div>
  );

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          {[
            { label: t('users.all'),      val: null,  cls: activeFilter === null  ? 'bg-purple-600 text-white border-purple-600' : '' },
            { label: t('users.active'),   val: true,  cls: activeFilter === true  ? 'bg-green-600 text-white border-green-600'   : '' },
            { label: t('users.inactive'), val: false, cls: activeFilter === false ? 'bg-red-600 text-white border-red-600'       : '' },
          ].map(b => (
            <button key={b.label}
              onClick={() => { setActiveFilter(b.val); setPage(1); }}
              className={`px-4 py-1.5 text-sm rounded-md border transition-colors ${b.cls || (isDark ? 'border-gray-700 text-gray-300 hover:bg-[#2a2f4a]' : 'border-gray-300 text-gray-700 hover:bg-gray-50')}`}>
              {b.label}
            </button>
          ))}

          {canCreateUser && (
            <button onClick={openNew} className="flex items-center gap-1.5 px-4 py-1.5 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors">
              <Plus size={14} /> {t('users.new')}
            </button>
          )}

          <button onClick={() => setShowFilters(v => !v)}
            className={`flex items-center gap-1.5 ${cls.btn} ${filterCount > 0 ? 'border-purple-500 text-purple-400' : ''}`}>
            <Filter size={14} />
            {t('users.filters')}
            {filterCount > 0 && <span className="bg-purple-600 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">{filterCount}</span>}
            {showFilters ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </div>

        <div className="flex items-center gap-2">
          <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{total} {t('users.users')}</span>
          <button onClick={loadUsers} className={cls.btn}><RefreshCw size={14} /></button>
        </div>
      </div>

      {/* Filters panel */}
      {showFilters && (
        <div className={`${cls.card} p-4`}>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            <div>
              <label className={cls.label}>{t('users.search')}</label>
              <input value={filters.search} onChange={e => setFilters(p => ({ ...p, search: e.target.value }))}
                className={cls.input} placeholder={t('users.searchPlaceholder')} />
            </div>
            <div>
              <label className={cls.label}>{t('users.role')}</label>
              <select value={filters.role} onChange={e => setFilters(p => ({ ...p, role: e.target.value }))} className={cls.input}>
                <option value="">{t('users.allRoles')}</option>
                {roleOptions.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            {isOwner && (
              <div>
                <label className={cls.label}>{t('users.office')}</label>
                <select value={filters.office} onChange={e => setFilters(p => ({ ...p, office: e.target.value }))} className={cls.input}>
                  <option value="">{t('users.allOffices')}</option>
                  {officeOpts.map(o => <option key={o.id} value={o.name}>{o.name}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className={cls.label}>{t('users.desk')}</label>
              <select value={filters.desk}
                onChange={e => setFilters(p => ({ ...p, desk: e.target.value }))}
                disabled={isOwner && !filters.office}
                className={(isOwner && !filters.office) ? cls.inputDis : cls.input}>
                <option value="">{(isOwner && !filters.office) ? t('users.selectOffice') : t('users.allDesks')}</option>
                {filterDeskOpts.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <label className={cls.label}>{t('users.team')}</label>
              <select value={filters.team}
                onChange={e => setFilters(p => ({ ...p, team: e.target.value }))}
                disabled={!filters.desk}
                className={!filters.desk ? cls.inputDis : cls.input}>
                <option value="">{filters.desk ? 'All teams' : '— select desk —'}</option>
                {filterTeamOpts.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
              </select>
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={applyFilters} className={cls.btnPrimary}>{t('users.apply')}</button>
            <button onClick={() => {
              setFilters({ search: '', role: '', office: '', desk: '', team: '' });
              setPage(1); setTimeout(loadUsers, 0);
            }} className={cls.btn}>{t('users.clear')}</button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className={`${cls.card} overflow-hidden`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={isDark ? 'bg-[#0a0e1a] border-b border-gray-800' : 'bg-gray-50 border-b border-gray-200'}>
              <tr>
                {['id','userName','email','role',...(isOwner?['office']:[]),'desk','team','clients','lastLogin','status','actions'].map(h => (
                  <th key={h} className={cls.th}>{t(`users.columns.${h}`)}</th>
                ))}
              </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'divide-gray-800' : 'divide-gray-100'}`}>
              {loading ? (
                <tr><td colSpan={isOwner?11:10} className={`${cls.td} text-center py-12`}>{t('users.loading')}</td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={isOwner?11:10} className={`${cls.td} text-center py-12 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{t('users.noUsers')}</td></tr>
              ) : users.map(u => {
                const isOwnerRow = u.role === 'owner';
                return (
                <tr key={u.id} className={isDark ? 'hover:bg-[#1a1f3a]' : 'hover:bg-gray-50'}>
                  <td className={cls.td}>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-600'}`}>
                      #{u.id.slice(0, 6)}
                    </span>
                  </td>
                  <td className={`${cls.td} font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{u.name}</td>
                  <td className={cls.td}>{u.email}</td>
                  <td className={cls.td}>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${ROLE_COLOR[u.role] || (isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600')}`}>
                      {roleLabels[u.role] || u.role}
                    </span>
                  </td>
                  {isOwner && <td className={cls.td}>{u.office || '—'}</td>}
                  <td className={cls.td}>{u.desk  || '—'}</td>
                  <td className={cls.td}>{u.team  || '—'}</td>
                  <td className={`${cls.td} text-center`}>{u.clientsActive || 0}</td>
                  <td className={cls.td}>{fmtDate(u.lastLogin)}</td>
                  <td className={cls.td}>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_BADGE(u.active)}`}>
                      {u.active ? t('users.active') : t('users.inactive')}
                    </span>
                  </td>
                  <td className={cls.td}>
                    <div className="flex items-center gap-2">
                      {canEditUser && <button onClick={() => openEdit(u)}
                        className={`p-1.5 rounded transition-colors ${isDark ? 'text-gray-400 hover:text-white hover:bg-gray-700' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'}`}>
                        <Pencil size={14} />
                      </button>}
                      {!isOwnerRow && (canEditUser || canDeleteUser) && (
                        <>
                          {canEditUser && <button onClick={() => toggleActive(u)}
                            className={`p-1.5 rounded text-xs transition-colors ${u.active ? 'text-orange-400 hover:bg-orange-500/10' : 'text-green-400 hover:bg-green-500/10'}`}
                            title={u.active ? t('users.deactivate') : t('users.activate')}>
                            {u.active ? '✕' : '✓'}
                          </button>}
                          {canDeleteUser && <button onClick={() => deleteUser(u)}
                            className="p-1.5 rounded transition-colors text-red-400 hover:bg-red-500/10">
                            <Trash2 size={14} />
                          </button>}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className={`flex items-center justify-between px-4 py-3 border-t ${isDark ? 'border-gray-800 text-gray-400' : 'border-gray-200 text-gray-600'}`}>
            <span className="text-sm">{t('users.showing')} {(page-1)*25+1}-{Math.min(page*25, total)} {t('users.of')} {total}</span>
            <div className="flex gap-1">
              <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page===1} className={`${cls.btn} disabled:opacity-40`}>‹</button>
              <span className={`px-3 py-1.5 text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{page}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page===totalPages} className={`${cls.btn} disabled:opacity-40`}>›</button>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className={`w-full max-w-md rounded-lg shadow-2xl ${isDark ? 'bg-[#1a1f3a]' : 'bg-white'}`}>
            <div className={`flex items-center justify-between px-6 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <h2 className={`text-base font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {editUser ? t('users.editUser') : t('users.newUser')}
              </h2>
              <button onClick={() => setShowModal(false)} className={isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500'}>✕</button>
            </div>
            <div className="px-6 py-5 space-y-3">
              {/* Name */}
              <div>
                <label className={cls.label}>{t('users.name')} *</label>
                <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className={cls.input} placeholder={t('users.fullName')} />
              </div>
              {/* Email */}
              <div>
                <label className={cls.label}>Email *</label>
                <input value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className={cls.input} placeholder="email@example.com" type="email" />
              </div>
              {/* Password */}
              <div>
                <label className={cls.label}>{editUser ? t('users.newPassword') : `${t('users.password')} *`}</label>
                <input
                  value={form.password}
                  onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                  className={cls.input}
                  placeholder={editUser ? t('users.keepPassword') : t('users.password')}
                  type="password"
                />
              </div>
              {/* Role */}
              <div>
                <label className={cls.label}>{t('users.role')}</label>
                <select
                  value={form.role}
                  onChange={e => {
                    if (editUser?.role === 'owner') return;
                    const role = e.target.value;
                    setForm(p => ({ ...p, role, department: roleDept[role] || '', team: '', desk: '', desks: [], office: '' }));
                  }}
                  disabled={editUser?.role === 'owner'}
                  className={cls.input}
                >
                  {(editUser?.role === 'owner' ? [{ value: 'owner', label: roleLabels.owner || 'Owner' }, ...roleOptions] : roleOptions)
                    .map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>

              {/* ── Team (TL / Agent roles only) ─────────────────────── */}
              {teamRoles.includes(form.role) && (
                <div>
                  <label className={cls.label}>{t('users.team')}</label>
                  <select
                    value={form.team}
                    onChange={e => {
                      const tName = e.target.value;
                      const t = allTeamOpts.find(x => x.name === tName);
                      setForm(p => ({ ...p, team: tName, desk: t?.deskName || '', office: t?.officeName || '' }));
                    }}
                    className={cls.input}
                  >
                    <option value="">— Select Team —</option>
                    {allTeamOpts
                      .filter(t => !t.department || t.department === form.department)
                      .map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                  </select>
                  {form.desk && (
                    <p className={`mt-1 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      Desk: <span className={isDark ? 'text-gray-300' : 'text-gray-600'}>{form.desk}</span>
                      {form.office && <> · Office: <span className={isDark ? 'text-gray-300' : 'text-gray-600'}>{form.office}</span></>}
                    </p>
                  )}
                </div>
              )}

              {/* ── Office (Head roles + Admin/CRM Manager — office-level) ── */}
              {(form.role === 'owner' || headRoles.includes(form.role) || ['admin', 'crm_manager'].includes(form.role)) && (
                <div>
                  <label className={cls.label}>{t('users.office')}</label>
                  {isOwner ? (
                    <select
                      value={form.office}
                      onChange={e => setForm(p => ({ ...p, office: e.target.value, desk: '', team: '' }))}
                      className={cls.input}
                    >
                      <option value="">— No office (global) —</option>
                      {officeOpts.map(o => <option key={o.id} value={o.name}>{o.name}</option>)}
                    </select>
                  ) : (
                    <div className={`px-3 py-2 rounded-md text-sm border ${isDark ? 'bg-[#060a14] border-gray-800 text-gray-400' : 'bg-gray-50 border-gray-200 text-gray-600'}`}>
                      {myOffice || '—'}
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-1">
                <button onClick={() => setShowModal(false)} className={cls.btn}>{t('users.cancel')}</button>
                <button onClick={saveUser} className={cls.btnPrimary}>{t('users.save')}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
